import cookie from 'cookie_js';

import EventSystem from 'https://cdn.venturz.co/modules/event-system/index.js';
import UserObserver from 'https://cdn.venturz.co/modules/user-observer/index.js';
import APIClient from 'https://cdn.venturz.co/modules/api-client/index.js';
import SystemDefinedUserStatusTracker from 'https://cdn.venturz.co/modules/system-defined-user-status-tracker/index.js';

import serverInterface from 'server-interface';
import { tryInsertFavicon } from 'utilities/dom';
import { findIndexByPropertyValue } from 'utilities/collection';
import { buildMenuList, determineCommunityFallbackPath } from './menu-list';

import * as errorCodes from 'error-codes/server';
import externalEventCodes from 'event-system/codes/external';
import internalEventCodes from 'event-system/codes/internal';

import userAccessScopes from 'constants/user-access-scopes';
import startupConfigurationNames from 'constants/startup-configuration-names';
import menuEventCodes from 'constants/menu-event-codes';

import FullscreenToggle from 'generics/components/right-panel-fullscreen-toggle';
import Canvas from 'generics/components/basic-text-editor-slate/elements/canvas/canvas';

import AccessController from './access-controller';
import Startup from './startup';
import Stores from './stores';
import CustomElementsManager from 'custom-elements-manager';
import Clock from './clock';
import DevicesManager from './devices-manager';
import Cache from 'cache';
import SocialNetworksManager from './social-networks-manager';
import DOMUtility from './dom-utility';
import System from './system';
import ExternalFontFamiliesManager from './external-font-families-manager';
import Prompts from './prompts';
import DialogsManager from './dialogs-manager';
import DataManagers from './data-managers';
import DynamicModulesManager from './dynamic-modules-manager';
import FavouritePageNodesManager from './favourite-page-nodes-manager';
import MarketProductsManager from './market-products-manager';
import UserSpaceConversationLabelsManager from './user-space-conversation-labels-manager';
import CalendarActivityTagsManager from './calendar-activity-tags-manager';
import CustomerLabelsManager from './customer-labels-manager';
import ApplicationLevelDialogs from './application-level-dialogs';
import SmallScreenManagement from './small-screen-management';
import ResourcesCacher from './resources-cacher';
import ApplicationLevelSpinner from './application-level-spinner';
import AudioManager from './audio-manager';
import NotificationsManager from './notifications-manager';
import AssistantManager from './assistant-manager';
import WindowMessagingManager from './window-messaging-manager';
import StaticData from './static-data';
import Stripe from './stripe';
import CSSUtility from './css-utility';
import FeaturesAvailability from './features-availability';

const helptapHosts = ['app.helptap.com', 'console.helptap.com',
  'os.helptap.com', 'app.development.helptap.com', 'console.development.helptap.com',
  'os.development.helptap.com', 'localhost:3001'
];

const platformOnlyPaths = [
  '/welcome',
  '/dashboard',
  '/pages',
  '/contacts',
  '/inbox',
  '/campaigns',
  '/finance'
];

class Application {

  constructor() {

    this.instanceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    this.initializing = false;
    this.initialized = false;
    this.authenticated = false;
    this.authorization = null;
    this.singular = false;
    this.helptapHost = false;
    this.helptapStartupHost = false;
    this.working = false;
    this.startupPatternPrefix = '';
    this.humanFriendlyStartupIdFromHost = '';
    this.userProfile = null;
    this.associatedStartups = [];
    this.fullMenuList = [];
    this.menuList = [];
    this.currentStartupId = null;
    this.currentCommunityId = null;
    this.startup = null;
    this.banner = null;
    this.startupModule = null;
    this.currentStartupPathPrefix = null;
    this.accessScope = null;
    this.helptapPermissions = [];
    this.startupUserObserver = null;
    this.platformUserObserver = null;
    this.socialNetworksManager = null;
    this.dynamicModulesManager = null;
    this.favouritePageNodesManager = null;
    this.onlineStatusTracker = null;
    this.serverInterface = serverInterface;
    // eslint-disable-next-line no-undef
    this.eventSystem = new EventSystem();
    this.cache = new Cache();
    this.socialNetworksManager = new SocialNetworksManager(this, this.serverInterface);

    this.computeHostInformation();
    this.registerEventListeners();

    this.managerNames = ['MarketProductsManager', 'PromotionCodesManager'];

    this.startupUserObserver = new UserObserver(true, {
      observerIdCookieKey: 'ht-uoid',
      siteSessionIdKey: 'site-sessionid',
      isPlatform: false,
      recordScreen: import.meta.env.REACT_APP_OBSERVE_USER !== 'false',
      dryRun: import.meta.env.REACT_APP_OBSERVE_USER === 'false'
    });
    this.platformUserObserver = window.pobs = new UserObserver(true, {
      observerIdCookieKey: 'platform-uoid',
      siteSessionIdKey: 'platform-sessionid',
      isPlatform: true,
      recordScreen: import.meta.env.REACT_APP_OBSERVE_USER !== 'false',
      dryRun: import.meta.env.REACT_APP_OBSERVE_USER === 'false'
    });
    this.fullscreenToggle = new FullscreenToggle();
    this.accessController = new AccessController(this);
    this.stores = new Stores(this, serverInterface);
    this.usersStore = this.stores.users;
    this.customElementsManager = new CustomElementsManager();
    this.clock = new Clock();
    this.devicesManager = new DevicesManager(this);
    this.domUtility = new DOMUtility();
    this.system = new System();
    this.externalFontFamiliesManager = new ExternalFontFamiliesManager(this);
    this.prompts = new Prompts();
    this.dialogsManager = new DialogsManager();
    this.dynamicModulesManager = new DynamicModulesManager();
    this.favouritePageNodesManager = new FavouritePageNodesManager(this);
    this.marketProductsManager = new MarketProductsManager(this);
    this.userSpaceConversationLabelsManager = new UserSpaceConversationLabelsManager(this);
    this.calendarActivityTagsManager = new CalendarActivityTagsManager(this);
    this.customerLabelsManager = new CustomerLabelsManager(this);
    this.applicationLevelDialogs = new ApplicationLevelDialogs(this);
    this.smallScreenManagement = new SmallScreenManagement();
    this.resourcesCacher = new ResourcesCacher(this);
    this.applicationLevelSpinner = new ApplicationLevelSpinner();
    this.audioManager = new AudioManager(this);
    this.notificationsManager = new NotificationsManager(this);
    this.windowMessagingManager = new WindowMessagingManager(this);
    this.assistantManager = new AssistantManager(this);
    this.staticData = new StaticData();
    this.dataManagers = new DataManagers(this); // All the data managers will be initialized and access from this going forward. Existing ones will be slowly moved into this.
    this.stripe = new Stripe(this);
    this.cssUtility = new CSSUtility(this);
    this.featuresAvailability = new FeaturesAvailability(this);

    this.customElementsManager.initialize();
    this.clock.start();

    window.zerozeroone = this;
    this.internalEventCodes = internalEventCodes;

    window.addEventListener('UserObserverInitialized', () => console.log('UserObserverInitialized'));

  }

  computeHostInformation() {

    this.helptapHost = helptapHosts.indexOf(window.location.host) !== -1;
    this.helptapStartupHost = this.computeIsHelptapStartupHost();
    this.singular = (this.helptapHost === false);
    this.startupPatternPrefix = this.computeStartupPatternPrefix();
    this.humanFriendlyStartupIdFromHost = this.computeHumanFriendlyStartupIdFromHost();

  }

  registerEventListeners() {

    function handleUserProfileUpdatedEvent(data) {

      this.userProfile = {
        ...this.userProfile,
        ...data
      };

    }

    this.eventSystem.internal.on(internalEventCodes.CURRENT_USER_PROFILE_UPDATED, handleUserProfileUpdatedEvent.bind(this));

    function handleStartupUserFullNameGenerated(startupUser) {

      if (this.userProfile._id !== startupUser._id) {
        return;
      }

      this.userProfile = {
        ...this.userProfile,
        ...startupUser
      };

    }

    this.eventSystem.internal.on(externalEventCodes.STARTUP_USER_FULL_NAME_GENERATED, handleStartupUserFullNameGenerated.bind(this));

    this.eventSystem.internal.on(externalEventCodes.FACEBOOK_ACCOUNT_INTEGRATION_DETAILS_UPDATED, this.updateCurrentStartup.bind(this));

  }

  getUser() {

    return this.userProfile;

  }

  setAuthenticated(authenticated) {
    this.authenticated = authenticated;
  }

  isAuthenticated() {
    return this.authenticated;
  }

  isUserAuthenticated() {
    return this.authenticated;
  }

  updateCurrentStartup(startup) {

    this.startup = {
      ...this.startup,
      ...startup
    };

    this.startupModule.setData(this.startup);

  }

  setCurrentStartupId(startupId) {

    this.currentStartupId = startupId;
    this.serverInterface.setCurrentStartupId(startupId);

  }

  setCurrentCommunityId(communityId) {

    this.currentCommunityId = communityId;
    this.serverInterface.setCurrentCommunityId(communityId);

  }

  getCookieDomain() {

    return import.meta.env.REACT_APP_HOST_ENDING.substring(1);

  }

  storeAuthorization(authorization) {

    this.authorization = authorization;

    return this.storeAuthorizationDataInCookie(authorization.userId, authorization.token, authorization.scope);

  }

  storeAuthorizationDataInCookie(userId, authorizationToken, accessScope) {

    const expires = 30;
    // let domain = this.getCookieDomain();

    const cookieSettings = {
      expires,
      path: '/'
      // domain
    };

    cookie.set({
      userId,
      authorizationToken,
      accessScope
    }, cookieSettings);

  }

  getAuthorizationFromCookie() {

    const token = cookie.get('authorizationToken') || '';
    const userId = cookie.get('userId') || '';
    const accessScope = cookie.get('accessScope') || '';

    return { token, userId, scope: accessScope };

  }

  async reinitializeStartupObserver() {

    // if (this.startupUserObserver === null) {
    //   await this.initializeUserObserver();
    // } else {
    //   this.startupUserObserver.reInitialize();
    // }

  }

  async logout(emitEvent = false) {

    await this.clearAuthorization();

    this.eventSystem.logout();

    setTimeout(() => window.location.reload(), 1);

  }

  clearAuthCookies() {

    const expires = 30;

    const cookieSettings = {
      expires,
      path: '/'
    };

    cookie.removeSpecific(['userId', 'authorizationToken', 'accessScope'], cookieSettings);

  }

  async clearAuthorization(reason) {

    const startup = this.startup;

    this.clearAuthCookies();

    if (this.authenticated === false) {
      return false;
    }

    this.setAuthenticated(false);

    await this.serverInterface.authentication.expireAuthorizationByToken(this.authorizationToken);
    this.serverInterface.configureStartupInterface(startup._id, startup.communities[0]._id);
    this.accessController.clear();

    this.authorizationToken = null;

    return true;

  }

  isInitialized() {

    return this.initialized;

  }

  async loadHelptapPermissions() {

    this.helptapPermissions = await this.serverInterface.system.getHelptapPermissions();

  }

  isProductsModuleEnabled() {

    // if (import.meta.env.REACT_APP_LOCAL === 'true') {
    //   return true;
    // }

    // if (this.startup._id !== '62288de27eace1fc04cdeea7') {
    //   return false;
    // }

    // if (this.userId !== '622f151af0ef419b1b3b447a') {
    //   return false;
    // }

    return true;

  }

  isCommunityEnabled() {

    return this.startup.communities[0].enabled !== false;

  }

  getHiddenMenuItemNames() {

    const hiddenMenuItemNames = [];

    if (this.isPaymentsModuleEnabled() === false) {
      hiddenMenuItemNames.push('Finance');
    }
    if (this.isCalendarModuleEnabled() === false) {
      hiddenMenuItemNames.push('Tasks');
    }
    if (this.isProductsModuleEnabled() === false) {
      hiddenMenuItemNames.push('Products');
    }
    if (this.isCommunityEnabled() === false) {
      hiddenMenuItemNames.push('Community');
    }

    return hiddenMenuItemNames;

  }

  async loadMenuList(bootstrapData) {

    this.fullMenuList = bootstrapData.menuList;
    this.menuList = buildMenuList(this.fullMenuList, this.getHiddenMenuItemNames());

  }

  refreshMenuList() {

    this.menuList = buildMenuList(this.fullMenuList, this.getHiddenMenuItemNames());

    this.eventSystem.internal.emit(menuEventCodes.MENU_LIST_CHANGED);

  }

  getCommunityFallbackPath() {

    return determineCommunityFallbackPath(this.menuList);

  }

  async tryConnectStartupUserAndObserver() {

    // The following code is moved to server when ObservatoriesService is implemented.
    // Due to lack of time, this code is not removed properly.
    // It must be removed when time was found.

    // const userObserverId = this.startupUserObserver.getObserverId();
    // const user = await this.serverInterface.currentUser.tryConnectStartupUserAndObserver(userObserverId);

    // this.startupUserObserver.setObserverId(user.observerId);
    // await this.eventSystem.tryAssociateWithObserverId({
    //   authorizationToken: this.getAuthorizationToken(),
    //   observerId: this.startupUserObserver.getObserverId()
    // });

  }

  async loadRecentStartups(bootstrapData) {

    if (this.accessScope !== userAccessScopes.HELPTAP) {
      return;
    }

    this.associatedStartups = bootstrapData.associatedStartups;

  }

  async loadAllAssociatedStartups() {

    if (this.accessScope !== userAccessScopes.HELPTAP) {
      return;
    }

    try {

      this.associatedStartups = await this.serverInterface.currentUser.getAllAssociatedStartups();

    } catch (exception) {

      // Customer users will not have permission to load associated startups.
      // This place needs some cleanup so, this cann will only be made for Helptap level users
      // eslint-disable-next-line no-console
      console.log(exception);

    }

  }

  getStartupById(startupId) {

    const associatedStartups = this.associatedStartups;

    for (let associatedStartup of associatedStartups) {

      if (associatedStartup._id === startupId) {
        return associatedStartup;
      }

    }

    return null;

  }

  getAllAssociatedStartups() {

    return this.associatedStartups;

  }

  addNewStartup(newStartup) {

    this.associatedStartups.push(newStartup);

  }

  getServerInterface() {

    return this.serverInterface;

  }

  getPublicServerInterface() {

    return this.serverInterface.public;

  }

  getStartupServerInterface() {

    return this.serverInterface.startup;

  }

  getCurrentUserServerInterface() {

    return this.serverInterface.currentUser;

  }

  async initializeServerInterface() {

    const application = this;

    function authorizationExpiredHandler(message) {

      if (application.initialized === true) {
        application.logout(true);

        application.serverInterface.configureStartupInterface(application.startup._id, application.startup.communities[0]._id);
      } else {
        application.clearAuthorization();
      }

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message,
        seconds: 5,
        type: 'error'
      });

      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: '/community'
      });

    }

    let eventSystem;

    if (import.meta.env.REACT_APP_DISABLE_COMMUNICATION_OVER_WEBSOCKET !== 'true') {
      eventSystem = this.eventSystem;
    }

    const apiClientConfiguration = {
      protocol: import.meta.env.REACT_APP_API_PROTOCOL,
      host: import.meta.env.REACT_APP_API_HOST,
      port: import.meta.env.REACT_APP_API_PORT,
      version: import.meta.env.REACT_APP_API_VERSION
    };
    // eslint-disable-next-line no-undef
    const apiClient = new APIClient(`${apiClientConfiguration.protocol}://${apiClientConfiguration.host}:${apiClientConfiguration.port}/${apiClientConfiguration.version}`, {
      [errorCodes.AUTHORIZATION_NOT_FOUND]: authorizationExpiredHandler.bind(null, 'Authorization cleared! Please login again.'),
      [errorCodes.AUTHORIZATION_EXPIRED]: authorizationExpiredHandler.bind(null, 'Authorization expired! Please login again.')
    }, this.authorization.token, Application.logError.bind(null, 'SERVER_INTERFACE_ERROR'), eventSystem, false);

    apiClient.setAuthorization(this.authorization.token);

    serverInterface.initialize(apiClient);

    await serverInterface.configureStartupInterface(this.startup._id, this.startup.communities[0]._id);
    await this.tryInitializeStartupAndCurrentUserServerInterfaces();

  }

  async activateUser(bootstrapData) {

    await this.accessController.initialize(bootstrapData);
    await this.loadRecentStartups(bootstrapData);
    await this.loadMenuList(bootstrapData);

    await this.tryConnectStartupUserAndObserver();

    const activated = await this.tryActivateMemberMode(bootstrapData);

    if (activated === true) {
      await this.startPlatformPageViewing();
    }

  }

  async startStartupPageViewing() {

    await this.startupUserObserver.start();

  }

  async stopStartupPageViewing() {

    await this.startupUserObserver.stop();

  }

  async startPlatformPageViewing() {

    await this.platformUserObserver.start();

  }

  async stopPlatformPageViewing() {

    await this.platformUserObserver.stop();

  }

  async initializeUserObserver() {

    const url = new URL(window.location.href);

    for (let platformOnlyPath of platformOnlyPaths) {

      if (url.pathname.indexOf(platformOnlyPath) === 0) {
        return;
      }

    }

    if (this.isAuthenticated() === true) {

      await this.eventSystem.tryAssociateWithObserverId({
        authorizationToken: this.getAuthorizationToken(),
        observerId: this.startupUserObserver.getObserverId()
      });

    }

  }

  async performPostStartupReadyActions() {

    if (this.isAuthenticated() === false) {
      // await this.initializeUserObserver();
    } else {
      // eslint-disable-next-line no-undef
      this.onlineStatusTracker = new SystemDefinedUserStatusTracker();
      this.onlineStatusTracker.initialize(this.serverInterface.apiClient.baseURL, this.startup._id, this.userId, this.authorizationToken);
      if (this.accessController.isMemberOrAdministrator() === false) {
        // await this.initializeUserObserver();
      }
    }

  }

  async tryActivateMemberMode(bootstrapData) {

    if (this.accessController.isMemberOrAdministrator() === false) {
      return false;
    }

    await this.startupModule.loadPrivateData(bootstrapData);

    await this.marketProductsManager.initialize(bootstrapData);
    await this.favouritePageNodesManager.initialize(bootstrapData);
    await this.dataManagers.initializePrivateManagers(bootstrapData);
    await this.assistantManager.loadAssistantData(bootstrapData);

    return true;

  }

  handleNetworkConnected() {
    // There is some problem with Websocket Connection.
    // Implemented behaviour is to have one connection per tab.
    // But, in reality, there are many connections per tab, which are created once network is disconnected.
    // this.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
    //   message: 'Network connected!',
    //   seconds: 5,
    //   type: 'info'
    // });

  }

  handleNetworkDisconnected() {
    // this.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
    //   message: 'Network disconnected! We will auto reconnect...',
    //   seconds: 5,
    //   type: 'error'
    // });
  }

  handleServerEvent(event) {
    this.eventSystem.internal.emit(event.name, event.payload);
  }

  handleServerAction(action) {
    this.reassociateEventSystem();
  }

  handleServerCallWhenOffline() {
    console.log('handleServerCallWhenOffline');
  }

  async tryInitializeAuthorization() {

    this.authorization = this.getAuthorizationFromCookie();

    if (this.authorization.token === '') {
      return false;
    }

    this.authorization = await this.serverInterface.authentication.getAuthorizationByUserTokenIfValid({
      token: this.authorization.token,
      projection: ['userId', 'scope', 'expiresAt']
    });

    if (this.authorization === void 0) {
      this.authorization = { token: '' };
    }

    return this.authorization.token !== '';

  }

  getAuthorization() {

    return this.authorization;

  }

  setUserData(data) {

    this.startup = data.startup;
    this.authorization = data.authorization;
    this.currentUser = this.userProfile = data.user;
    this.authorizationValid = data.authorizationValid;
    this.banner = data.banner;

  }

  async initializeTransport() {

    const onConnected = this.handleNetworkConnected.bind(this);
    const onDisconnected = this.handleNetworkDisconnected.bind(this);
    const onEvent = this.handleServerEvent.bind(this);
    const onAction = this.handleServerAction.bind(this);
    const onServerCallWhenOffline = this.handleServerCallWhenOffline.bind(this);

    const configuration = {
      url: '/__nativesocket',
      version: 'v1',
      onConnected,
      onDisconnected,
      onEvent,
      onAction,
      onServerCallWhenOffline,
      onValidAuth: () => {
        if (this.authorizationValid === void 0 || this.authorizationValid === false) {
          return;
        }
        // window.location.reload();
      },
      onInvalidAuth: () => {
        window.location.reload();
      },
      onReconnected: this.setUserData.bind(this)
    };

    const data = await this.eventSystem.initializeWebSocketClient(configuration);

    this.setUserData(data);

    await this.initializeServerInterface();

  }

  async doInitialize() {

    try {

      this.initialized = false;
      this.initializing = true;

      await this.initializeTransport();

      const bootstrapData = await serverInterface.startup.bootstrapBrowserClient();

      this.system.setSupportedFonts(bootstrapData.supportedFonts);
      this.externalFontFamiliesManager.initialize();
      this.prompts.initialize();
      this.dialogsManager.initialize();

      await this.activateStartup(bootstrapData);
      await this.initializePublicModules();

      if (this.authenticated === true) {

        await this.activateUser(bootstrapData);

      }
      await this.performPostStartupReadyActions();

      this.initialized = true;
      this.initializing = false;

    } catch (exception) {

      console.log({ exception });

    }

  }

  async initialize() {

    if (this.initializing === true) {
      return this.initializingPromise;
    }

    if (this.initialized === true) {
      return this.initializingPromise;
    }

    this.initializingPromise = this.doInitialize();

    return this.initializingPromise;

  }

  async activateStartup(bootstrapData) {

    const startup = this.startup;

    tryInsertFavicon(startup);

    this.startupModule = new Startup(this.startup, bootstrapData, {
      application: this,
      serverInterface: this.serverInterface,
      eventSystem: this.eventSystem,
      bootstrapData
    });
    window.startup = {
      _id: startup._id
    };
    await this.startupModule.initialize();

    Canvas.configureControls(startup);

  }

  setCurrentStartup(startup) {

    this.startup = {
      ...this.startup,
      ...startup
    };

  }

  getCurrentStartup() {

    return this.startup;

  }

  getStartupColors() {

    const startup = this.startup;
    const theme = startup.theme;
    const themeColors = theme.colors;

    const colors = [themeColors.primary, themeColors.secondary, themeColors.tertiary, themeColors.background, themeColors.secondaryBackground, themeColors.text];

    return colors;

  }

  isStartupDomainOrSubDomain() {
    return false;
  }

  getAuthenticatorURL() {

    return import.meta.env.REACT_APP_AUTHENTICATOR_URL + `?origin=${application.getHostURL()}&path=${window.location.pathname}&observerId=${this.startupUserObserver.getObserverId()}`;

  }

  getHostURL() {

    return window.location.protocol + '//' + window.location.host;

  }

  isHelptapStartupHost() {

    return this.helptapStartupHost;

  }

  computeIsHelptapStartupHost() {

    if (this.isHelptapHost() === true) {
      return false;
    }

    const expectedSuffix = import.meta.env.REACT_APP_HOST_ENDING;
    const currentHost = window.location.host;

    return (currentHost.indexOf(expectedSuffix) + expectedSuffix.length) === currentHost.length;

  }

  computeStartupPatternPrefix() {

    if (this.isHelptapHost() === false) {
      return '/';
    }

    return '/s/:humanFriendlyId';

  }

  getStartupPatternPrefix() {

    return this.startupPatternPrefix;

  }

  isHelptapHost() {
    return this.helptapHost;
  }

  computeHumanFriendlyStartupIdFromHost() {

    let humanFriendlyStartupId = '';

    if (this.isHelptapHost() === true) {
      return humanFriendlyStartupId;
    }

    if (this.isHelptapStartupHost() === false) {
      return window.location.host;
    }

    return window.location.host.replace(import.meta.env.REACT_APP_HOST_ENDING, '');

  }

  getHumanFriendlyStartupIdFromHost() {

    return this.humanFriendlyStartupIdFromHost;

  }

  getCurrentStartupPath(humanFriendlyStartupId) {

    if (this.isHelptapHost() === true) {
      return `/s/${humanFriendlyStartupId}`;
    }

    return '/';

  }

  isAssociatedStartup(humanFriendlyStartupId) {

    const associatedStartups = this.getAllAssociatedStartups();

    for (let associatedStartup of associatedStartups) {

      if (associatedStartup.humanFriendlyId === humanFriendlyStartupId) {
        return true;
      }

    }

    return false;

  }

  isSingular() {
    return this.singular;
  }

  setCurrentStartupPathPrefix(currentStartupPathPrefix) {
    this.currentStartupPathPrefix = currentStartupPathPrefix;
  }

  getCurrentStartupPathPrefix() {
    return this.currentStartupPathPrefix;
  }

  getApplicationProtocol() {
    return import.meta.env.REACT_APP_APPLICATION_PROTOCOL;
  }

  getStartupURL(humanFriendlyStartupId) {

    return `${this.getApplicationProtocol()}://${humanFriendlyStartupId}${import.meta.env.REACT_APP_HOST_ENDING}`;

  }

  isValidAuthorizationExists() {

    const authorization = this.authorization;

    return authorization.token !== '';

  }

  async tryInitializeStartupAndCurrentUserServerInterfaces() {

    const authorization = this.authorization;

    if (this.authorizationValid === false) {
      this.setAuthenticated(false); // I do this because, I might want call #tryInitializeStartupAndCurrentUserServerInterfaces from elsewhere
      return;
    }

    this.userId = this.userProfile._id;
    this.authorizationToken = authorization.token;
    this.accessScope = authorization.scope;

    serverInterface.configureStartupAndCurrentUserServerInterfaces(this.startup, this.userId, this.accessScope);

    this.setAuthenticated(true);

  }

  getAuthorizationToken() {

    return this.authorizationToken;

  }

  isPaymentsModuleEnabled() {

    const REACT_APP_PAYMENTS_ENABLED_STARTUPS = import.meta.env.REACT_APP_PAYMENTS_ENABLED_STARTUPS;

    if (REACT_APP_PAYMENTS_ENABLED_STARTUPS !== 'all') {

      const startupIds = REACT_APP_PAYMENTS_ENABLED_STARTUPS.split(',');

      if (startupIds.indexOf(this.startup._id) === -1) {
        return false;
      }

    }

    return true;

  }

  isCalendarModuleEnabled() {

    const REACT_APP_PAYMENTS_ENABLED_STARTUPS = import.meta.env.REACT_APP_PAYMENTS_ENABLED_STARTUPS;

    if (REACT_APP_PAYMENTS_ENABLED_STARTUPS !== 'all') {

      const startupIds = REACT_APP_PAYMENTS_ENABLED_STARTUPS.split(',');

      if (startupIds.indexOf(this.startup._id) === -1) {
        return false;
      }

    }

    return true;

  }

  handleStartViewLoaded() {

    this.resourcesCacher.start();

  }

  setWorking(working) {

    this.working = working;

    if (this.working === true) {
      this.applicationLevelSpinner.show();
    } else {
      this.applicationLevelSpinner.hide();
    }

  }

  getPhoneConfiguration() {

    const configurations = this.getCurrentStartup().configurations;

    for (let configuration of configurations) {

      if (configuration.name === startupConfigurationNames.PHONE) {
        return configuration;
      }

    }

  }

  setPhoneConfiguration(configuration) {

    const configurations = this.getCurrentStartup().configurations;
    const phoneConfigurationIndex = findIndexByPropertyValue(configurations, 'name', startupConfigurationNames.PHONE);

    configurations[phoneConfigurationIndex] = configuration;

  }

  async reassociateEventSystem() {

    await this.tryInitializeStartupAndCurrentUserServerInterfaces();
    await this.eventSystem.tryAssociateWithObserverId({
      authorizationToken: this.getAuthorizationToken(),
      observerId: this.startupUserObserver.getObserverId()
    });

  }

  getInstanceId() {

    return this.instanceId;

  }

  // Vite sets DEV only for the dev server; any `vite build` output has it false, so this cannot
  // leak into a deployed bundle. REACT_APP_LOCAL, which this used to read, is not defined in any
  // env file.
  isLocal() {
    return import.meta.env.DEV === true;
  }

  isBetaFeaturesEnabled() {

    if (this.isLocal() === true) {
      return true;
    }

    const betaFeaturesEnabledStartups = [
      '6231d2411367959b5b62f053',
      '62288de27eace1fc04cdeea7'
    ];

    return betaFeaturesEnabledStartups.includes(this.getCurrentStartup()._id);

  }

  async initializePublicModules() {

    await this.dataManagers.initializePublicManagers();

  }

  isVenturz() {
    return this.getCurrentStartup()._id === import.meta.env.REACT_APP_VENTURZ_ID;
  }

  static serializeError(error) {
    if (!error || typeof error !== 'object') {
      return { message: String(error) };
    }

    const serialized = {
      name: error.name ?? 'Error',
      message: error.message ?? 'Unknown error',
      stack: error.stack ?? null,
    };

    try {
      for (const key of Object.getOwnPropertyNames(error)) {
        const value = error[key];
        if (typeof value !== 'function') {
          serialized[key] = value;
        }
      }
    } catch (_) {
      // ignore inaccessible props
    }

    return serialized;
  }

  static logError(type, error) {
    // eslint-disable-next-line no-console
    console.error(error);
    // ---- Endpoint ----
    const protocol = import.meta.env.REACT_APP_API_PROTOCOL || 'https';
    const host = import.meta.env.REACT_APP_API_HOST;
    const port = import.meta.env.REACT_APP_API_PORT;
    const version = import.meta.env.REACT_APP_API_VERSION || 'v1';
    const url = `${protocol}://${host}${port ? `:${port}` : ''}/${version}/client-errors`;

    // ---- Error ----
    const errorDetails = this.serializeError(error?.error || error);
    if (error && error.componentStack) errorDetails.componentStack = error.componentStack;

    // ---- Context (no cookies) ----
    const uaData = navigator?.userAgentData ? {
        brands: navigator.userAgentData.brands,
        mobile: navigator.userAgentData.mobile,
        platform: navigator.userAgentData.platform,
      } :
      null;

    const payload = {
      type,
      errorDetails,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      userAgent: navigator?.userAgent ?? null,
      userAgentData: uaData,
      platform: navigator?.platform ?? null,
      vendor: navigator?.vendor ?? null,
      language: navigator?.language ?? null,
      languages: navigator?.languages ?? null,
      screen: { width: window?.screen?.width ?? null, height: window?.screen?.height ?? null },
      host: typeof window !== 'undefined' ? window.location.host : null, // host + port
    };

    const body = JSON.stringify(payload);

    // ---- Prefer sendBeacon during background/unload; fallback to fetch(keepalive) ----
    const canBeacon = typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
    const isBackgrounding =
      (typeof document !== 'undefined' && document.visibilityState === 'hidden') ||
      (typeof performance !== 'undefined' && performance?.navigation?.type === 'reload');

    // ~64KB typical limit for sendBeacon payloads — stay under a safe threshold
    const withinBeaconLimit = body.length <= 60 * 1024;

    if (canBeacon && isBackgrounding && withinBeaconLimit) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'application/json; charset=UTF-8' }));
      if (ok) return; // delivered
      // fall through to fetch if beacon failed
    }

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // keepalive lets the request continue during page unload in supporting browsers
      keepalive: true,
    }).catch((err) => {
      console.error('Error reporting to /client-errors:', err);
    });
  }

}

const application = new Application();

export default application;